import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ShieldCheck, ShieldOff, KeyRound } from 'lucide-react'
import { useEffect, useState } from 'react'

import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  EmptyState,
  Modal,
  PageHeader,
  SkeletonRows,
  TextField,
  mutedTextClass,
} from '@/components/layout/ui'
import { cn } from '@/lib/cn'
import { fieldErrorFor, messageFor } from '@/lib/errors'
import { formatDate, formatRelative } from '@/lib/format'
import { queryKeys } from '@/lib/queryClient'
import { useSession } from '@/lib/session'
import { adminApi } from './api'
import { Pager, SearchInput, TableScroll, Td, Th } from './parts'
import type { AdminUser } from './types'

/**
 * `/admin/users` — every account, and who administers the platform.
 *
 * The admin toggle is the most dangerous control in the product: it hands
 * somebody the ability to comp any workspace and to promote anyone else. So it
 * is not a switch that acts on click. It opens a dialog that names the person,
 * states what the change means in a sentence, and requires a reason — the same
 * shape as the grant dialog, because it is the same class of action.
 *
 * Two rules the server enforces and this screen surfaces rather than duplicates:
 * you cannot demote yourself, and the last administrator cannot be demoted. The
 * self-demotion case is disabled here because it is knowable locally; the
 * last-admin case is NOT pre-empted, because only the server can count, and a
 * guess that disagreed with it would be worse than the honest 422.
 */
export default function AdminUsersPage() {
  const { user } = useSession()
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pending, setPending] = useState<{ user: AdminUser; promote: boolean } | null>(null)
  const [resetting, setResetting] = useState<AdminUser | null>(null)

  const users = useQuery({
    queryKey: queryKeys.adminUsers(search, page),
    queryFn: ({ signal }) => adminApi.users({ q: search || undefined, page }, { signal }),
    placeholderData: (previous) => previous,
  })

  function changeSearch(next: string) {
    setSearch(next)
    setPage(1)
  }

  const rows = users.data?.data ?? []

  return (
    <>
      <PageHeader
        title="Users"
        description="Every account on this deployment, and which of them administer the platform."
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          value={search}
          onChange={changeSearch}
          label="Search users"
          placeholder="Search by name or email"
        />
      </div>

      {users.isError ? (
        <Alert tone="danger">{messageFor(users.error)}</Alert>
      ) : (
        <Card>
          {users.isPending ? (
            <div className="p-4">
              <SkeletonRows rows={6} />
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              title={search ? 'No accounts match that search' : 'No accounts yet'}
              action={search ? <Button onClick={() => changeSearch('')}>Clear search</Button> : undefined}
            />
          ) : (
            <TableScroll>
              <thead>
                <tr>
                  <Th>Person</Th>
                  <Th numeric>Workspaces</Th>
                  <Th>Joined</Th>
                  <Th>Last seen</Th>
                  <Th>Platform admin</Th>
                  <Th className="text-right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const isSelf = row.id === user?.id
                  return (
                    <tr key={row.id} className="transition hover:bg-ink-50">
                      <Td>
                        <span className="flex items-center gap-2.5">
                          <Avatar name={row.name} size="sm" />
                          <span className="min-w-0">
                            <span className="block truncate font-medium">
                              {row.name}
                              {isSelf ? (
                                <span className={cn('ml-1.5 text-xs font-normal', mutedTextClass)}>
                                  (you)
                                </span>
                              ) : null}
                            </span>
                            <span className={cn('block truncate text-xs', mutedTextClass)}>
                              {row.email}
                            </span>
                          </span>
                        </span>
                      </Td>
                      <Td numeric>{row.workspace_count}</Td>
                      <Td>
                        <span className="whitespace-nowrap tabular-nums">
                          {formatDate(row.created_at)}
                        </span>
                      </Td>
                      <Td>
                        <span className="whitespace-nowrap">
                          {row.last_seen_at ? formatRelative(row.last_seen_at) : '—'}
                        </span>
                      </Td>
                      <Td>
                        {row.is_admin ? (
                          <Badge tone="warning">Platform admin</Badge>
                        ) : (
                          <span className={mutedTextClass}>—</span>
                        )}
                      </Td>
                      <Td className="text-right">
                        {row.is_admin ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={isSelf}
                            title={
                              isSelf
                                ? 'You cannot remove your own platform administrator access.'
                                : undefined
                            }
                            className="text-red-600 hover:bg-red-50 hover:text-red-700"
                            onClick={() => setPending({ user: row, promote: false })}
                          >
                            <ShieldOff className="size-3.5" aria-hidden="true" />
                            Remove admin
                          </Button>
                        ) : (
                          <Button size="sm" onClick={() => setPending({ user: row, promote: true })}>
                            <ShieldCheck className="size-3.5" aria-hidden="true" />
                            Make admin
                          </Button>
                        )}
                        {/* Not offered for yourself: Account settings already
                            changes your own password and asks for the current
                            one, and the server refuses this route for the
                            caller regardless. */}
                        {!isSelf && (
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Set this user's password and sign them out everywhere"
                            onClick={() => setResetting(row)}
                          >
                            <KeyRound className="size-3.5" aria-hidden="true" />
                            Set password
                          </Button>
                        )}
                      </Td>
                    </tr>
                  )
                })}
              </tbody>
            </TableScroll>
          )}

          {users.data ? (
            <Pager
              page={users.data.page}
              perPage={users.data.per_page}
              total={users.data.total}
              onPage={setPage}
              noun="accounts"
            />
          ) : null}
        </Card>
      )}

      <AdminFlagDialog pending={pending} onClose={() => setPending(null)} />
      <SetPasswordDialog user={resetting} onClose={() => setResetting(null)} />
    </>
  )
}

function AdminFlagDialog({
  pending,
  onClose,
}: {
  pending: { user: AdminUser; promote: boolean } | null
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (pending) setReason('')
  }, [pending])

  const change = useMutation({
    mutationFn: () => {
      if (!pending) throw new Error('Nothing to change.')
      return adminApi.setAdmin(pending.user.id, {
        is_admin: pending.promote,
        reason: reason.trim(),
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin })
      onClose()
    },
  })

  if (!pending) return null
  const { user: target, promote } = pending

  return (
    <Modal
      open
      onClose={onClose}
      title={promote ? `Make ${target.name} a platform admin` : `Remove ${target.name}'s admin access`}
      footer={
        <>
          <Button onClick={onClose} disabled={change.isPending}>
            Cancel
          </Button>
          <Button
            variant={promote ? 'primary' : 'danger'}
            onClick={() => change.mutate()}
            loading={change.isPending}
            disabled={reason.trim().length === 0}
          >
            {promote ? 'Make admin' : 'Remove admin'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Alert tone={promote ? 'warning' : 'info'}>
          {promote
            ? 'A platform administrator can see every workspace and every account on this deployment, comp any workspace onto a paid plan, and promote or demote other administrators.'
            : `${target.name} will keep their workspaces and their roles in them, and lose access to this console.`}
        </Alert>

        <p className={cn('text-sm', mutedTextClass)}>
          {target.email} · {target.workspace_count}{' '}
          {target.workspace_count === 1 ? 'workspace' : 'workspaces'}
        </p>

        {change.error ? <Alert tone="danger">{messageFor(change.error)}</Alert> : null}

        <TextField
          label="Reason"
          value={reason}
          maxLength={255}
          placeholder={promote ? 'Second operator for on-call' : 'Left the company'}
          error={fieldErrorFor(change.error, 'reason')}
          hint="Required. Recorded in the audit log against your name."
          onChange={(event) => setReason(event.target.value)}
        />
      </div>
    </Modal>
  )
}

/**
 * Setting somebody's password.
 *
 * This is account takeover, performed on purpose, so the dialog says exactly
 * what will happen rather than only asking for confirmation: the person is
 * signed out everywhere, and whoever runs this has to hand them the password
 * through some channel that is not this screen.
 *
 * The password is shown while typing. Masking it would be security theatre —
 * the admin is CHOOSING it and has to read it out — and a mistyped password
 * that nobody can see locks the user out a second time.
 */
export function SetPasswordDialog({
  user,
  onClose,
}: {
  user: AdminUser | null
  onClose: () => void
}) {
  const client = useQueryClient()
  const [password, setPassword] = useState('')
  const [reason, setReason] = useState('')

  const submit = useMutation({
    mutationFn: () =>
      adminApi.setPassword(user!.id, { password, reason }),
    onSuccess: async () => {
      setPassword('')
      setReason('')
      // The audit page and the user list both change.
      await client.invalidateQueries({ queryKey: queryKeys.admin })
      onClose()
    },
  })

  if (!user) return null

  return (
    <Modal
      open
      onClose={onClose}
      title={`Set a password for ${user.name}`}
      description={`${user.email} will be signed out of every browser session immediately.`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={password.length < 8 || reason.trim().length === 0 || submit.isPending}
            onClick={() => submit.mutate()}
          >
            {submit.isPending ? 'Setting…' : 'Set password'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {submit.isError && <Alert tone="danger">{messageFor(submit.error)}</Alert>}
        <TextField
          label="New password"
          value={password}
          // Deliberately not a password field: the admin is choosing it and has
          // to read it back to somebody.
          type="text"
          autoComplete="off"
          spellCheck={false}
          onChange={(event) => setPassword(event.target.value)}
          hint="At least 8 characters. You will need to pass this to them yourself."
          error={fieldErrorFor(submit.error, 'password')}
        />
        <TextField
          label="Reason"
          value={reason}
          placeholder="Lost access to their mailbox"
          onChange={(event) => setReason(event.target.value)}
          hint="Recorded in the audit trail against your name."
          error={fieldErrorFor(submit.error, 'reason')}
        />
      </div>
    </Modal>
  )
}
